import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

// API URL - uses api subdomain in production, localhost in development
const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://api.pythagoreschool.com/upload.php'
    : 'http://localhost:3001/upload';

const HomeworkManagement = ({ classes = [] }) => {
    const [homeworks, setHomeworks] = useState([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [subject, setSubject] = useState('');
    const [grade, setGrade] = useState('');
    const [dueDate, setDueDate] = useState('');

    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [removeFile, setRemoveFile] = useState(false);
    const [editing, setEditing] = useState(false);
    const [currentHomeworkId, setCurrentHomeworkId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [homeworkToDelete, setHomeworkToDelete] = useState(null);
    const [subjects, setSubjects] = useState([]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    // Calculate pagination
    const totalPages = Math.ceil(homeworks.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentHomeworks = homeworks.slice(startIndex, endIndex);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        const fetchHomeworks = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'homeworks'));
                const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort by most recent first
                data.sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                    return dateB - dateA;
                });
                setHomeworks(data);
            } catch (error) {
                console.error('Error fetching homeworks:', error);
            }
        };
        const fetchSubjects = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'artifacts/school-management-system-app/public/data/subjects'));
                const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('Fetched subjects:', data); // Log fetched subjects for debugging
                setSubjects(data);
            } catch (error) {
                console.error('Error fetching subjects:', error);
            }
        };
        fetchHomeworks();
        fetchSubjects();
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(selectedFile.type)) {
            alert('نوع الملف غير مدعوم. يُسمح فقط بالصور (JPG, PNG, GIF) أو PDF');
            e.target.value = '';
            return;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (selectedFile.size > maxSize) {
            alert('حجم الملف كبير جداً. الحد الأقصى 10MB');
            e.target.value = '';
            return;
        }

        setFile(selectedFile);

        // Create preview for images
        if (selectedFile.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setFilePreview(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePreview(null);
    };

    const setTodayAsDueDate = () => {
        const today = new Date().toISOString().split('T')[0];
        setDueDate(today);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let fileURL = '';
            if (file) {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(API_URL, {
                    method: 'POST',
                    body: formData
                });

                if (!response.ok) {
                    throw new Error('Upload failed');
                }

                const data = await response.json();
                fileURL = data.fileUrl;
            }

            // Find subject name by id
            const selectedSubject = subjects.find(s => s.id === subject);
            const homeworkData = {
                title,
                description,
                subject: selectedSubject ? selectedSubject.name : '',
                grade,
                dueDate,

                file: fileURL,
            };

            if (editing) {
                // Update existing homework
                const homeworkRef = doc(db, 'homeworks', currentHomeworkId);
                if (file) {
                    homeworkData.file = fileURL;
                } else {
                    const existingHw = homeworks.find(hw => hw.id === currentHomeworkId);
                    homeworkData.file = existingHw?.file || '';
                }
                await updateDoc(homeworkRef, homeworkData);
                setHomeworks(homeworks.map(hw => hw.id === currentHomeworkId ? { ...hw, ...homeworkData } : hw));
            } else {
                // Add new homework
                const newHomeworkData = { ...homeworkData, createdAt: new Date() };
                const docRef = await addDoc(collection(db, 'homeworks'), newHomeworkData);
                setHomeworks([...homeworks, { id: docRef.id, ...newHomeworkData }]);
            }
        } catch (error) {
            console.error('Error saving homework:', error);
        }
        // Clear form
        setTitle('');
        setDescription('');
        setSubject('');
        setGrade('');
        setDueDate('');
        setFile(null);
        setFilePreview(null);
        setEditing(false);
        setCurrentHomeworkId(null);
    };

    const handleEdit = (homework) => {
        setEditing(true);
        setCurrentHomeworkId(homework.id);
        setTitle(homework.title);
        setDescription(homework.description);
        // Try to match subject by name to id
        const foundSubject = subjects.find(s => s.name === homework.subject);
        setSubject(foundSubject ? foundSubject.id : '');
        setGrade(homework.grade);
        setDueDate(homework.dueDate);
        setFile(null);
        setRemoveFile(false);
    };

    const handleDelete = async (id) => {
        setHomeworkToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!homeworkToDelete) return;
        try {
            await deleteDoc(doc(db, 'homeworks', homeworkToDelete));
            setHomeworks(homeworks.filter(hw => hw.id !== homeworkToDelete));
        } catch (error) {
            console.error('Error deleting homework:', error);
        }
        setShowDeleteModal(false);
        setHomeworkToDelete(null);
    };

    const cancelDelete = () => {
        // file: fileURL, // Removed to handle file logic below
        setHomeworkToDelete(null);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setTitle('');
        setDescription('');
        setSubject('');
        setGrade('');
        setDueDate('');
        setFile(null);
        setFilePreview(null);
        setCurrentHomeworkId(null);
    }

    return (
        <div className="p-6 bg-gray-100 min-h-screen" dir="rtl">
            <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">إدارة الواجبات المدرسية</h2>

                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h3 className="text-xl font-semibold mb-4">{editing ? 'تعديل الواجب' : 'إضافة واجب جديد'}</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">العنوان</label>
                            <input
                                type="text"
                                placeholder="عنوان الواجب"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">المادة الدراسية</label>
                                <select
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                    required
                                >
                                    <option value="">اختر المادة الدراسية</option>
                                    {subjects.length > 0 ? (
                                        subjects.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))
                                    ) : (
                                        <option value="" disabled>لا توجد مواد دراسية متاحة</option>
                                    )}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">الصف</label>
                                <select
                                    value={grade}
                                    onChange={(e) => setGrade(e.target.value)}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                    required
                                >
                                    <option value="">اختر الصف</option>
                                    {classes.map((c) => (
                                        <option key={c.id || c.name} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">تاريخ التسليم</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                                <button type="button" onClick={setTodayAsDueDate} className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 whitespace-nowrap transition">
                                    واجب اليوم
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">الوصف</label>
                            <textarea
                                placeholder="وصف الواجب"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                rows="3"
                            ></textarea>
                        </div>

                        {/* File attachment section */}
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">
                                إرفاق ملف (اختياري)
                                <span className="text-xs text-gray-500 mr-2">صورة أو PDF - الحد الأقصى 10MB</span>
                            </label>
                            <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                onChange={handleFileChange}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />

                            {/* File preview */}
                            {file && (
                                <div className="mt-3 p-3 bg-gray-50 border rounded">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {filePreview ? (
                                                <div className="mb-2">
                                                    <img
                                                        src={filePreview}
                                                        alt="Preview"
                                                        className="max-h-40 rounded border"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex items-center text-red-600 mb-2">
                                                    <svg className="w-8 h-8 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                                    </svg>
                                                    <span className="font-medium">ملف PDF</span>
                                                </div>
                                            )}
                                            <div className="text-sm text-gray-600">
                                                <strong>{file.name}</strong><br />
                                                الحجم: {(file.size / 1024).toFixed(2)} KB
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="إزالة الملف"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Show existing file when editing */}
                            {editing && !file && homeworks.find(hw => hw.id === currentHomeworkId)?.file && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-sm text-gray-700">
                                        📎 يوجد ملف مرفق حالي
                                        <button
                                            type="button"
                                            onClick={() => setRemoveFile(true)}
                                            className="mr-2 text-red-600 hover:underline"
                                        >
                                            حذف الملف
                                        </button>
                                    </p>
                                    {removeFile && (
                                        <div className="mt-1 text-red-600 text-xs">⚠️ سيتم حذف الملف المرفق عند الحفظ</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-4 mt-4">
                        <button type="submit" className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600">
                            {editing ? 'تحديث الواجب' : 'إضافة الواجب'}
                        </button>
                        {editing && (
                            <button onClick={handleCancelEdit} type="button" className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600">
                                إلغاء
                            </button>
                        )}
                    </div>
                </form>

                <div className="bg-white rounded-lg shadow-md">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-xl font-semibold">قائمة الواجبات</h3>
                        {homeworks.length > 0 && (
                            <div className="text-sm text-gray-600">
                                المجموع: {homeworks.length} واجب
                            </div>
                        )}
                    </div>
                    <div className="divide-y">
                        <ul className="space-y-4">
                            {currentHomeworks.map(hw => (
                                <li key={hw.id} className="p-4 border rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div className="text-right">
                                        <p className="font-bold text-lg">{hw.title}</p>
                                        <p className="text-gray-600">{hw.subject} - {hw.grade}</p>
                                        <p className="text-sm text-gray-500">تاريخ التسليم: {hw.dueDate}</p>
                                        <p className="mt-2">{hw.description}</p>
                                        {hw.file && (
                                            <div className="mt-2">
                                                <a href={hw.file} target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 mt-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition">
                                                    تحميل الملف المرفق
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 mt-2 md:mt-0">
                                        <button onClick={() => handleEdit(hw)} className="bg-yellow-500 text-white py-1 px-3 rounded hover:bg-yellow-600">تعديل</button>
                                        <button onClick={() => handleDelete(hw.id)} className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600">حذف</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        ) : (
                        <p className="text-right text-gray-500">لا توجد واجبات حالياً.</p>
                    )}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="mt-6 p-4 border-t bg-gray-50">
                                <div className="flex items-center justify-between">
                                    {/* عرض معلومات الصفحة */}
                                    <div className="text-sm text-gray-600">
                                        عرض {startIndex + 1} - {Math.min(endIndex, homeworks.length)} من {homeworks.length}
                                    </div>

                                    {/* أزرار التنقل */}
                                    <div className="flex gap-2 items-center">
                                        {/* زر الصفحة السابقة */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`px-3 py-1 rounded ${currentPage === 1
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                }`}
                                        >
                                            السابق
                                        </button>

                                        {/* أرقام الصفحات */}
                                        <div className="flex gap-1">
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`px-3 py-1 rounded ${currentPage === page
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            ))}
                                        </div>

                                        {/* زر الصفحة التالية */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`px-3 py-1 rounded ${currentPage === totalPages
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                }`}
                                        >
                                            التالي
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md text-center">
                            <h3 className="text-xl font-bold mb-4 text-red-700">تأكيد حذف الواجب</h3>
                            <p className="mb-6 text-gray-700">هل أنت متأكد أنك تريد حذف هذا الواجب؟ لا يمكن التراجع عن هذه العملية.</p>
                            <div className="flex justify-center gap-4">
                                <button onClick={cancelDelete} className="bg-gray-400 text-white px-6 py-2 rounded-lg font-bold hover:bg-gray-500">إلغاء</button>
                                <button onClick={confirmDelete} className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700">تأكيد الحذف</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HomeworkManagement;